import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import superAdminApi from "../../../lib/superAdminApi";
import SuperAdminLayout from "../../../components/SuperAdminLayout";
import { ArrowLeft, Pencil, UserCheck, UserX, X } from "lucide-react";
import { toast } from "react-toastify";

interface StoreAssignment {
  is_active?: boolean | null;
  Role?: { name?: string | null } | null;
  Store?: { id: string; name?: string | null; location?: string | null } | null;
}

interface UserDetail {
  id: string;
  email: string;
  username: string;
  firstname?: string | null;
  lastname?: string | null;
  othername?: string | null;
  phone_number?: string | null;
  is_active: boolean;
  is_deleted: boolean;
  created_at?: string | null;
  updated_at?: string | null;
  role?: string | null;
  Business?: {
    id: string;
    name?: string | null;
    website?: string | null;
    description?: string | null;
    logo?: string | null;
    BusinessPackageSubscription?: {
      id: string;
      is_active?: boolean | null;
      billing_cycle?: string | null;
      start_date?: string | null;
      end_date?: string | null;
      Package?: { id: string; name?: string | null } | null;
      FreeTrial?: { start_date?: string | null; end_date?: string | null }[];
    } | null;
  } | null;
  StoreAssignment?: StoreAssignment[];
}

interface EditForm {
  firstname: string;
  lastname: string;
  email: string;
  username: string;
  phone_number: string;
}

const SuperAdminUserDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState<EditForm>({
    firstname: "",
    lastname: "",
    email: "",
    username: "",
    phone_number: "",
  });
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);

  const fetchUser = () => {
    setLoading(true);
    superAdminApi
      .get(`/superadmin/users/${id}`)
      .then((res) => setUser(res.data.user))
      .catch((err) => toast.error(err.response?.data?.message ?? "Failed to load user"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (id) fetchUser();
  }, [id]);

  const openEditModal = () => {
    if (!user) return;
    setEditForm({
      firstname: user.firstname ?? "",
      lastname: user.lastname ?? "",
      email: user.email ?? "",
      username: user.username ?? "",
      phone_number: user.phone_number ?? "",
    });
    setShowEditModal(true);
  };

  const handleEditSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setSaving(true);
    try {
      await superAdminApi.put(`/superadmin/users/${user.id}`, editForm);
      toast.success("User updated successfully");
      setShowEditModal(false);
      fetchUser();
    } catch (err: any) {
      toast.error(err.response?.data?.message ?? "Failed to update user");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async () => {
    if (!user) return;
    const endpoint = user.is_active
      ? `/superadmin/users/${user.id}/deactivate`
      : `/superadmin/users/${user.id}/activate`;
    setToggling(true);
    try {
      await superAdminApi.put(endpoint);
      toast.success(`User ${user.is_active ? "deactivated" : "activated"}`);
      fetchUser();
    } catch (err: any) {
      toast.error(err.response?.data?.message ?? "Action failed");
    } finally {
      setToggling(false);
    }
  };

  return (
    <SuperAdminLayout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/superadmin/users")}
              className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">User Detail</h1>
              <p className="text-gray-500 text-sm mt-0.5">View and manage user information</p>
            </div>
          </div>
          {user && (
            <div className="flex items-center gap-2">
              <button
                onClick={openEditModal}
                className="flex items-center gap-1.5 px-3 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-500 transition-colors"
              >
                <Pencil className="w-3.5 h-3.5" />
                Edit
              </button>
              <button
                onClick={toggleActive}
                disabled={toggling}
                title={user.is_active ? "Deactivate user" : "Activate user"}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 ${
                  user.is_active
                    ? "bg-red-50 text-red-600 hover:bg-red-100"
                    : "bg-green-50 text-green-700 hover:bg-green-100"
                }`}
              >
                {user.is_active ? (
                  <>
                    <UserX className="w-3.5 h-3.5" />
                    Deactivate
                  </>
                ) : (
                  <>
                    <UserCheck className="w-3.5 h-3.5" />
                    Activate
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="space-y-5 animate-pulse">
            {/* Profile card skeleton */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
              <div className="h-4 bg-gray-200 rounded w-40 mb-5" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i}>
                    <div className="h-3 bg-gray-200 rounded w-20 mb-1.5" />
                    <div className="h-4 bg-gray-200 rounded w-36" />
                  </div>
                ))}
              </div>
            </div>
            {/* Business card skeleton */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
              <div className="h-4 bg-gray-200 rounded w-44 mb-5" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i}>
                    <div className="h-3 bg-gray-200 rounded w-20 mb-1.5" />
                    <div className="h-4 bg-gray-200 rounded w-28" />
                  </div>
                ))}
              </div>
            </div>
            {/* Store table skeleton */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-50">
                <div className="h-4 bg-gray-200 rounded w-32" />
              </div>
              <div className="p-5 space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="h-4 bg-gray-200 rounded w-32" />
                    <div className="h-4 bg-gray-200 rounded w-24" />
                    <div className="h-4 bg-gray-200 rounded w-16" />
                    <div className="h-5 bg-gray-200 rounded-full w-14" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : !user ? (
          <div className="flex items-center justify-center h-48 text-gray-400 text-sm">User not found.</div>
        ) : (
          <div className="space-y-5">
            {/* Profile card */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
              <h2 className="text-sm font-semibold text-gray-700 mb-4">Profile Information</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Full Name</p>
                  <p className="text-sm text-gray-900">
                    {[user.firstname, user.othername, user.lastname].filter(Boolean).join(" ") || "—"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Email</p>
                  <p className="text-sm text-gray-900">{user.email}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Username</p>
                  <p className="text-sm text-gray-900">{user.username || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Phone</p>
                  <p className="text-sm text-gray-900">{user.phone_number || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Role</p>
                  <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                    {user.role ?? "—"}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Status</p>
                  <span
                    className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                      user.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"
                    }`}
                  >
                    {user.is_active ? "Active" : "Inactive"}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Joined</p>
                  <p className="text-sm text-gray-900">
                    {user.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Last Updated</p>
                  <p className="text-sm text-gray-900">
                    {user.updated_at ? new Date(user.updated_at).toLocaleDateString() : "—"}
                  </p>
                </div>
              </div>
            </div>

            {/* Business association */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
              <h2 className="text-sm font-semibold text-gray-700 mb-4">Business Association</h2>
              {user.Business ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-400 mb-0.5">Business Name</p>
                      <button
                        onClick={() => navigate(`/superadmin/businesses/${user.Business!.id}`)}
                        className="text-sm text-indigo-600 hover:underline font-medium"
                      >
                        {user.Business.name ?? "Unnamed"}
                      </button>
                    </div>
                    {user.Business.website && (
                      <div>
                        <p className="text-xs text-gray-400 mb-0.5">Website</p>
                        <a
                          href={user.Business.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-indigo-600 hover:underline"
                        >
                          {user.Business.website}
                        </a>
                      </div>
                    )}
                  </div>
                  {user.Business.BusinessPackageSubscription && (
                    <div className="mt-3 pt-3 border-t border-gray-50">
                      <p className="text-xs text-gray-400 mb-2">Subscription</p>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                          <p className="text-xs text-gray-400 mb-0.5">Package</p>
                          <p className="text-sm text-gray-900">
                            {user.Business.BusinessPackageSubscription.Package?.name ?? "—"}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 mb-0.5">Billing Cycle</p>
                          <p className="text-sm text-gray-900 capitalize">
                            {user.Business.BusinessPackageSubscription.billing_cycle ?? "—"}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 mb-0.5">Sub Status</p>
                          <span
                            className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                              user.Business.BusinessPackageSubscription.is_active
                                ? "bg-green-100 text-green-700"
                                : "bg-red-100 text-red-600"
                            }`}
                          >
                            {user.Business.BusinessPackageSubscription.is_active ? "Active" : "Expired"}
                          </span>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 mb-0.5">Start Date</p>
                          <p className="text-sm text-gray-900">
                            {user.Business.BusinessPackageSubscription.start_date
                              ? new Date(user.Business.BusinessPackageSubscription.start_date).toLocaleDateString()
                              : "—"}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 mb-0.5">End Date</p>
                          <p className="text-sm text-gray-900">
                            {user.Business.BusinessPackageSubscription.end_date
                              ? new Date(user.Business.BusinessPackageSubscription.end_date).toLocaleDateString()
                              : "—"}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-400">No business associated.</p>
              )}
            </div>

            {/* Store assignments */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-50">
                <h2 className="text-sm font-semibold text-gray-700">Store Assignments</h2>
              </div>
              {!user.StoreAssignment || user.StoreAssignment.length === 0 ? (
                <p className="px-5 py-4 text-sm text-gray-400">No store assignments.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-100">
                      <tr className="text-left text-gray-500">
                        <th className="px-4 py-3 font-medium">Store</th>
                        <th className="px-4 py-3 font-medium">Location</th>
                        <th className="px-4 py-3 font-medium">Role</th>
                        <th className="px-4 py-3 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {user.StoreAssignment.map((assignment, idx) => (
                        <tr key={assignment.Store?.id ?? idx} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3 font-medium text-gray-900">
                            {assignment.Store?.name ?? "—"}
                          </td>
                          <td className="px-4 py-3 text-gray-500">
                            {assignment.Store?.location ?? "—"}
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                              {assignment.Role?.name ?? "—"}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                                assignment.is_active
                                  ? "bg-green-100 text-green-700"
                                  : "bg-red-100 text-red-600"
                              }`}
                            >
                              {assignment.is_active ? "Active" : "Inactive"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-md">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900">Edit User</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="p-1 rounded-lg text-gray-400 hover:bg-gray-100 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={handleEditSave} className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">First Name</label>
                  <input
                    type="text"
                    value={editForm.firstname}
                    onChange={(e) => setEditForm((f) => ({ ...f, firstname: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Last Name</label>
                  <input
                    type="text"
                    value={editForm.lastname}
                    onChange={(e) => setEditForm((f) => ({ ...f, lastname: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Username</label>
                <input
                  type="text"
                  value={editForm.username}
                  onChange={(e) => setEditForm((f) => ({ ...f, username: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Phone Number</label>
                <input
                  type="text"
                  value={editForm.phone_number}
                  onChange={(e) => setEditForm((f) => ({ ...f, phone_number: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-500 transition-colors disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </SuperAdminLayout>
  );
};

export default SuperAdminUserDetail;
